import { isObject } from 'lodash-es';
import type { Directive, DirectiveBinding } from 'vue';
import { useWTooltip } from './useWTooltip';
import { nextFrame } from './w-tooltip.helpers';
import {
  type Options,
  type Placement,
  TOOLTIP_DEBOUNCE_TIME,
  TOOLTIP_SELECTOR,
  tooltipPlacements,
  type Value,
} from './w-tooltip.types';

const { showTooltip, hideTooltip, isTooltipHovered, tooltipTrigger } = useWTooltip();

// We use a WeakMap here to store each element's events and content
// to ensure that the tooltip data is garbage collected
// when the trigger element is no longer in use.
const tooltipData = new WeakMap<
  HTMLElement,
  {
    onMouseEnter: EventListener;
    onMouseLeave: EventListener;
    onFocus: EventListener;
    onBlur: EventListener;
    onClick: EventListener;
    options: Options;
    timerId: ReturnType<typeof setTimeout> | null;
  }
>();

const isAnyTooltipContainerHovered = () => {
  const tooltipContainers = document.querySelectorAll<HTMLElement>(TOOLTIP_SELECTOR);

  return Array.from(tooltipContainers).some((tooltipContainer) => tooltipContainer.matches(':hover'));
};

const clearTimer = (el: HTMLElement) => {
  const data = tooltipData.get(el);

  if (data?.timerId) {
    clearTimeout(data.timerId);
    data.timerId = null;
  }
};

// Debounces tooltip show/hide so rapid pointer movement between trigger and tooltip
// doesn't cause flicker or stale hide/show races.
const scheduleDebounced = (el: HTMLElement, callback: () => void) => {
  const data = tooltipData.get(el);

  if (!data) return;

  clearTimer(el);

  data.timerId = setTimeout(() => {
    const latestData = tooltipData.get(el);
    if (latestData) latestData.timerId = null;
    callback();
  }, TOOLTIP_DEBOUNCE_TIME);
};

const createOptions = (binding: DirectiveBinding<Value, Placement>): Options => {
  const { value, modifiers } = binding;

  const options: Options = isObject(value) ? value : { content: null };
  const isPlacement = (placement: string): placement is Placement => {
    return tooltipPlacements.includes(placement as Placement);
  };
  const modifierPlacement = Object.keys(modifiers).find(isPlacement);

  options.content = typeof value === 'string' ? value : value?.content || null;
  options.placement = modifierPlacement || options.placement || 'top';

  return options;
};

const bind = (el: HTMLElement, binding: DirectiveBinding<Value, Placement>) => {
  const onShow = () => {
    // If the content is null or empty, we don't want to show the tooltip
    scheduleDebounced(el, () => {
      const latestData = tooltipData.get(el);

      if (!latestData?.options || !latestData.options.content) return;

      showTooltip({ trigger: el, options: latestData.options });
    });
  };

  const onMouseEnter = () => {
    onShow();
  };

  const onFocus = () => {
    onShow();
  };

  const onMouseLeave = async () => {
    const data = tooltipData.get(el);
    clearTimer(el);

    if (!data) return;

    const hide = () => {
      if (tooltipTrigger.value === el) {
        hideTooltip();
      }
    };

    if (data.options.stayOpenOnHover === true) {
      await nextFrame();
      const isHovered = isTooltipHovered.value || isAnyTooltipContainerHovered();

      if (!isHovered) {
        scheduleDebounced(el, hide);
      }
    } else {
      scheduleDebounced(el, hide);
    }
  };

  const onBlur = () => {
    clearTimer(el);

    scheduleDebounced(el, () => {
      if (tooltipTrigger.value === el) {
        hideTooltip();
      }
    });
  };

  const onClick = () => {
    clearTimer(el);

    if (tooltipTrigger.value === el) {
      isTooltipHovered.value = false;
      hideTooltip();
    }
  };

  // Initialize the tooltip data for this element
  tooltipData.set(el, {
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    onClick,
    options: createOptions(binding),
    timerId: null,
  });

  el.addEventListener('mouseenter', onMouseEnter);
  el.addEventListener('mouseleave', onMouseLeave);
  el.addEventListener('focus', onFocus);
  el.addEventListener('blur', onBlur);
  el.addEventListener('click', onClick);
};

const destroy = (el: HTMLElement) => {
  const data = tooltipData.get(el);

  if (data) {
    clearTimer(el);

    if (tooltipTrigger.value === el) {
      isTooltipHovered.value = false;
      hideTooltip();
    }

    el.removeEventListener('mouseenter', data.onMouseEnter);
    el.removeEventListener('mouseleave', data.onMouseLeave);
    el.removeEventListener('focus', data.onFocus);
    el.removeEventListener('blur', data.onBlur);
    el.removeEventListener('click', data.onClick);
    tooltipData.delete(el);
  }
};

const vWTooltip: Directive<HTMLElement> = {
  mounted: bind,
  beforeUnmount(el: HTMLElement) {
    destroy(el);
  },
  updated: (el: HTMLElement, binding: DirectiveBinding<Value, Placement>) => {
    const data = tooltipData.get(el);
    const { value, oldValue } = binding;

    // Only update the content if the value has changed
    if (data && JSON.stringify(value) !== JSON.stringify(oldValue)) {
      data.options = createOptions(binding);
    }
  },
};

export { vWTooltip };
